/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Card from "components/card";
import {
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { FaAngleLeft } from "react-icons/fa";
import { MdOutlineContentCopy } from "react-icons/md";
import { Tooltip, IconButton } from "@mui/material";
import SweetAlert2 from "react-sweetalert2";
import { Auth } from "aws-amplify";

import AgentDetailTab from "./components/AgentDetailTab";
import AgentCommissionTab from "./components/AgentCommissionTab";
import AgentUsersTab from "./components/AgentUsersTab";
import AgentSubaccountsTab from "./components/AgentSubaccountsTab";
import AgentFeesTab from "./components/AgentFeesTab";

import { useParams, useNavigate } from "react-router-dom";

interface PayloadType {
  [key: string]: any;
}

const AgentDetail = ({ userPermissions }: { userPermissions: any }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isFetchingBalance, setIsFetchingBalance] = useState<boolean>(false);
  const [isFetchingCommission, setIsFetchingCommission] = useState<boolean>(false);
  const [agentDetailForEdit, setAgentDetailForEdit] = useState<any>(null);
  const [balancesData, setBalancesData] = useState<any>(null);
  const [commissionData, setCommissionData] = useState<any>([]);
  const [assignedSubaccountIds, setAssignedSubaccountIds] = useState<any>(null);
  const [isFetchAssignedFeeGroups, setIsFetchAssignedFeeGroups] =
    useState<boolean>(false);
  const [swalProps, setSwalProps] = useState<any>({});
  const [currentUsername, setCurrentUsername] = useState<string>("");
  const [countries, setCountries] = useState<any>(null);
  const [dialcodeList, setDialcodeList] = useState<any>(null);
  const [dialcodeAutoCompleteOptions, setDialcodeAutoCompleteOptions] =
    useState<any>(null);

  const navigate = useNavigate();
  const { agent_id } = useParams();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  useEffect(() => {
    const fetchData = async () => {
      await fetchAccountDetail(agent_id);
      await getCommissionEarned(agent_id);
      await fetchCountries();
      const userInfo = await Auth.currentAuthenticatedUser();
      setCurrentUsername(userInfo.username);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (dialcodeList !== null) {
      let rebuildListArray: any = [];

      Object.entries(dialcodeList).forEach(([key, value]: [any, any]) => {
        let newObj = {
          value: key,
          label: value,
        };
        rebuildListArray.push(newObj);
      });

      setDialcodeAutoCompleteOptions(rebuildListArray);
    }
  }, [dialcodeList]);

  const fetchAccountDetail = async (
    accountId: string | undefined,
    onlyBalance: boolean = false
  ) => {
    if (!accountId) return;

    try {
      if (onlyBalance) {
        setIsFetchingBalance(true);
      } else {
        setAgentDetailForEdit(null);
        setIsLoading(true);
      }

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-by-id?subaccountid=${accountId}&with_balance=true&include_assigned_feegroup=true`
      );

      let data = response.data.subaccount?.payload;
      let balancesData = response.data.balances;

      // Check if agent data exists, if not redirect to agents listing
      if (!data) {
        console.error("Agent not found");
        navigate(`/${userRole}/agents`);
        return;
      }

      const idsOnly = data.subaccounts?.map((item: any) => item.id);
      setAssignedSubaccountIds(idsOnly);

      setAgentDetailForEdit(data);
      setBalancesData(balancesData);
      setIsLoading(false);
      setIsFetchingBalance(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching data", error);
      // Redirect to agents listing page on error
      navigate(`/${userRole}/agents`);
    }
  };

  const fetchAccountDetailToGetAssignedSubaccount = async (
    accountId: string
  ) => {
    setAssignedSubaccountIds(null);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-by-id?subaccountid=${accountId}&with_balance=true`
      );

      let accountData = response.data.subaccount?.payload;
      const idsOnly = accountData.subaccounts?.map((item: any) => item.id);
      setAssignedSubaccountIds(idsOnly);
    } catch (error) {
      console.error(
        "Error fetching data for get assigned subaccount ids",
        error
      );
    }
  };

  const fetchAccountDetailToGetAssignedFeeGroup = async (accountId: string) => {
    setIsFetchAssignedFeeGroups(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-by-id?subaccountid=${accountId}&with_balance=true&include_assigned_feegroup=true`
      );

      let data = response.data.subaccount?.payload;
      setAgentDetailForEdit(data);
      setIsFetchAssignedFeeGroups(false);
    } catch (error) {
      setIsFetchAssignedFeeGroups(false);
      console.error(
        "Error fetching data for get assigned subaccount ids",
        error
      );
    }
  };

  const getCommissionEarned = async (agentId: string | undefined) => {
    if (!agentId) return;

    setIsFetchingCommission(true);
    try {
      const queryObject: PayloadType = {
        transaction_type: "reseller_fee_received",
        id: agent_id,
        type: "agent"
      };

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-subaccount-balance?${queryString}`
      );

      const balances = response.data?.balances;

      if (balances) {
        setCommissionData(balances);
      }

      setIsFetchingCommission(false);
    } catch (error: any) {
      console.error("Error get commission data", error);
      setIsFetchingCommission(false);
      return false;
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL + `/api/datasource/countries`
      );

      let countries = response?.data;
      let dialcodeList: any = [];
      if (countries.length > 0) {
        countries.forEach((c: any) => {
          if (c.dialcode !== null) {
            if (c.dialcode in dialcodeList) {
              dialcodeList[c.dialcode] =
                dialcodeList[c.dialcode] + ", " + c.country_name;
            } else {
              dialcodeList[c.dialcode] =
                "(+" + c.dialcode + ") " + c.country_name;
            }
          }
        });
      }

      const sortedDialcodeList: any = [];
      Object.keys(dialcodeList)
        .sort()
        .forEach((key: any) => {
          sortedDialcodeList[key] = dialcodeList[key];
        });

      setCountries(countries);
      setDialcodeList(sortedDialcodeList);
    } catch (error) {
      console.error("Error fetching countries", error);
    }
  };

  const handleClickBack = () => {
    navigate(`/${userRole}/agents`);
  };

  const handleClickCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  // Define tab configuration with permission checks
  const tabConfig = useMemo(() => [
    { label: "Details", show: true },
    { label: "Commissions", show: true },
    { label: "Fees", show: true },
    { label: "Subaccounts", show: true },
    { label: "Users", show: true },
  ], [userPermissions]);

  // Filter visible tabs
  const visibleTabs = useMemo(() => {
    return tabConfig.filter((tab) => tab.show);
  }, [tabConfig]);

  // Render tab content based on label
  const renderTabContent = (label: string) => {
    switch (label) {
      case "Details":
        return (
          <AgentDetailTab
            detail={agentDetailForEdit}
            setSwalProps={setSwalProps}
            dialcodeList={dialcodeList}
            dialcodeAutoCompleteOptions={dialcodeAutoCompleteOptions}
          />
        );
      case "Commissions":
        return (
          <AgentCommissionTab
            data={commissionData}
            isFetchingCommission={isFetchingCommission}
          />
        );
      case "Users":
        return (
          <AgentUsersTab currentUsername={currentUsername} agentId={agent_id} />
        );
      case "Subaccounts":
        return (
          <AgentSubaccountsTab
            detail={agentDetailForEdit}
            fetchAccountDetailToGetAssignedSubaccount={
              fetchAccountDetailToGetAssignedSubaccount
            }
            assignedSubaccountIds={assignedSubaccountIds}
          />
        );
      case "Fees":
        return (
          <AgentFeesTab
            detail={agentDetailForEdit}
            setSwalProps={setSwalProps}
            triggerReload={fetchAccountDetailToGetAssignedFeeGroup}
            isFetchAssignedFeeGroups={isFetchAssignedFeeGroups}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Card className="card card-pad !rounded-xl">
        {!isLoading && agentDetailForEdit !== null ? (
          <>
            <div className="mb-3 flex items-center">
              <div>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => handleClickBack()}
                  className="
                    mb-1 inline-flex items-center gap-1
                    text-sm font-medium text-gray-700
                    transition hover:text-gray-700
                    disabled:cursor-not-allowed disabled:opacity-40
                    dark:text-gray-400 dark:hover:text-white
                  "
                >
                  <FaAngleLeft className="text-base" />
                  <span>Agents</span>
                </button>

                <h3 className="text-xl font-semibold text-navy-700">
                  {agentDetailForEdit.suba_name}
                </h3>
                <div className="flex items-center gap-1 text-sm text-gray-500">
                  ID: {agent_id}
                  <Tooltip title="Copy Agent ID" arrow placement="top">
                    <IconButton
                      size="small"
                      onClick={() => handleClickCopy(agent_id)}
                    >
                      <MdOutlineContentCopy className="h-3.5 w-3.5 text-gray-500" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div>
            <div
              className="mb-3 mt-3 h-5 w-36 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
            <div
              className="mb-3 mt-3 h-5 animate-pulse rounded-md bg-gray-200"
              style={{ animationDuration: "0.6s" }}
            ></div>
          </div>
        )}

        <Tabs className="mt-3">
          <TabList className="rounded-tl-custom rounded-tr-custom scrollbar-hide flex flex-wrap overflow-x-auto sm:flex-nowrap">
            {visibleTabs.map((tab, index) => (
              <Tab
                key={index}
                className={`child-tab rounded-tl-custom rounded-tr-custom sm:text-md whitespace-nowrap bg-white px-4 py-3 pt-4 text-sm text-navy-700 sm:px-6 sm:py-4 sm:pt-6 ${
                  isLoading || isProcessing || agentDetailForEdit === null
                    ? "pointer-events-none opacity-50"
                    : ""
                }`}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels className="rounded-bl-custom rounded-br-custom border-t border-gray-200 bg-white">
            {visibleTabs.map((tab, index) => (
              <TabPanel
                key={index}
                className={
                  tab.label === "Details"
                    ? "rounded-bl-custom rounded-br-custom px-4 py-8"
                    : ""
                }
              >
                {renderTabContent(tab.label)}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </Card>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default AgentDetail;
